import React, {useCallback} from "react";
import {memo} from "react";
import {RootState} from "../../../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import {addCardActions} from "../addCard.slice";
import { styled } from "styled-components";
import { ThemeProps } from "../../../styles/theme";
import { useTheme } from "../../../styles/ThemeContext";

const selector = (state: RootState) => ({
    invertedMarkup: state.addCard.invertedMarkup,
});

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
`;

const Editor = styled.textarea<ThemeProps>`
    height: 290px;
    width: 100%;
    resize: none;
    margin: 0;
    padding: 10px;
    background-color: ${(props: ThemeProps) => props.theme.primaryBackground};
    color: ${(props: ThemeProps) => props.theme.inputText};
    border: ${(props: ThemeProps) => props.theme.primaryBorder} 1px solid;
    border-width: 1px 1px 2px 1px;
    outline: none;
    font-size: 14px;
    font-family: monospace;
    box-sizing: border-box;
    display: block;
`;

const InvertedMarkupEditorComponent: React.FC = memo(() => {
    const stateProps = useSelector(selector);
    const dispatch = useDispatch();
    const { theme } = useTheme();

    const textChangedHandler = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        dispatch(addCardActions.setInvertedMarkup(event.target.value));
    }, [dispatch]);

    const pasteHandler = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const position = (event.target as HTMLTextAreaElement).selectionStart;
        if (position === undefined)
            return;

        const items = event.clipboardData.items;
        if (items.length !== 1 || !items[0].type.startsWith('image/'))
            return;

        const blob = items[0].getAsFile();
        if (blob === null) return;
        
        const reader = new FileReader();
        reader.onload = function(readerEvent)
        {
            const dataUrl = readerEvent.target!.result!.toString();
            dispatch(addCardActions.addImage({
                cursorPosition: position,
                dataUrl,
            }))
        };
        reader.readAsDataURL(blob);
    }, [dispatch]);

    return (
        <Root>
            <Editor
                theme={theme}
                value={stateProps.invertedMarkup}
                onChange={textChangedHandler}
                onPaste={pasteHandler}
            />
        </Root>
    );
});

InvertedMarkupEditorComponent.displayName = 'InvertedMarkupEditorComponent';

export { InvertedMarkupEditorComponent as InvertedMarkupEditor };
